import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Loader2, Sparkles, ArrowLeft } from "lucide-react";
import Footer from "../components/Footer";

const Welcome = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [editHero, setEditHero] = useState(false);
  const [heroPosition, setHeroPosition] = useState({ x: 50, y: 53 });
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate("/sanctuary");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/request-magic-link`, { email });
      setIsSent(true);
      setHasError(false);
      toast.success("Demande envoyée.");
    } catch (error) {
      setHasError(true);
      setIsSent(false);
      toast.error("Votre demande n'a pas pu être validée.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2] relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-60"
        style={{ 
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_site-cocoon/artifacts/mhlz4cgb_%20-12.jpg)`,
          backgroundPosition: `${heroPosition.x}% ${heroPosition.y}%`
        }}
      />
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#F9F7F2]/60 to-[#F9F7F2]" />

      {/* Petit mode édition pour la photo hero (centrage) */}
      <div className="absolute top-6 right-6 z-20 flex flex-col items-end gap-1">
        <button
          type="button"
          onClick={() => setEditHero(!editHero)}
          className="text-[0.6rem] tracking-[0.18em] uppercase px-3 py-1 rounded-full bg-[#3F2A24]/80 text-[#F3E5CF] shadow-sm hover:bg-[#6E3A1E]"
        >
          {editHero ? "Mode édition photo : ON" : "Mode édition photo : OFF"}
        </button>

        {editHero && (
          <div className="flex flex-col items-end gap-1 bg-[#3F2A24]/85 px-2 py-2 rounded-2xl text-[#F3E5CF] text-[0.6rem]">
            <div className="flex justify-between gap-2 w-full">
              <span className="tracking-[0.15em] uppercase opacity-80">Centrer la photo</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => setHeroPosition(pos => ({ ...pos, y: Math.max(20, pos.y - 3) }))}
                  className="px-2 py-1 rounded-full bg-[#6E3A1E] hover:bg-[#C5A059] hover:text-[#3F2A24]"
                >
                  ↑ Haut
                </button>
                <button
                  type="button"
                  onClick={() => setHeroPosition({ x: 50, y: 50 })}
                  className="px-2 py-1 rounded-full bg-[#6E3A1E] hover:bg-[#C5A059] hover:text-[#3F2A24]"
                >
                  • Centre
                </button>
                <button
                  type="button"
                  onClick={() => setHeroPosition(pos => ({ ...pos, y: Math.min(80, pos.y + 3) }))}
                  className="px-2 py-1 rounded-full bg-[#6E3A1E] hover:bg-[#C5A059] hover:text-[#3F2A24]"
                >
                  ↓ Bas
                </button>
              </div>
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => setHeroPosition(pos => ({ ...pos, x: Math.max(20, pos.x - 3) }))}
                  className="px-2 py-1 rounded-full bg-[#6E3A1E] hover:bg-[#C5A059] hover:text-[#3F2A24]"
                >
                  ← Gauche
                </button>
                <button
                  type="button"
                  onClick={() => setHeroPosition(pos => ({ ...pos, x: Math.min(80, pos.x + 3) }))}
                  className="px-2 py-1 rounded-full bg-[#6E3A1E] hover:bg-[#C5A059] hover:text-[#3F2A24]"
                >
                  Droite →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
        <div className="max-w-md w-full animate-fade-in-up">
          {/* Logo/Brand */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C5A059]/10 mb-6 animate-breathing">
              <Sparkles className="w-8 h-8 text-[#C5A059]" strokeWidth={1.5} />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#3F2A24] mb-2 tracking-tight">
              COCON SACRÉ
            </h1>
            <p className="text-xs tracking-[0.35em] uppercase font-medium text-[#4B3329] mb-1">
              MON ESPACE NUMÉRIQUE
            </p>
            <p className="text-[0.65rem] tracking-[0.25em] uppercase font-medium text-[#F3E5CF]">
              INDONESIE • MAI – JUIN 2026
            </p>
          </div>

          {/* Divider removed */}

          {!isSent && !hasError ? (
            <>
              {/* Intro Text */}
              <p className="text-center text-[#4B3329] mb-8 leading-relaxed">
                Cet espace est réservé<br />
                aux âmes déjà inscrites à COCON SACRÉ.
              </p>
              <p className="text-center text-[#4B3329] mb-4 leading-relaxed">
                Pour y entrer, indique simplement ton email.<br />
                Un lien d’accès personnel te sera envoyé.
              </p>
              <p className="text-center text-[#C5A059] text-xs tracking-[0.35em] uppercase mb-6">
                ⸻
              </p>

              {/* Form */}
              <div className="h-8" />
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ton@email.com"
                    className="input-sacred text-center text-[#3F2A24] placeholder:text-[#8C6A4A]"
                    required
                    data-testid="email-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-sacred w-full flex items-center justify-center gap-2"
                  data-testid="request-link-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Envoi en cours...</span>
                    </>
                  ) : (
                    <span>Recevoir mon lien d’accès</span>
                  )}
                </button>
              </form>
            </>
          ) : null}

          {isSent && !hasError && (
            /* Success State - Cas 1 */
            <div className="text-center animate-fade-in-up">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-[#C5A059]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <h2 className="font-serif text-2xl text-[#3F2A24] mb-4">
                Merci pour ta demande 🤍
              </h2>
              <p className="text-[#4B3329] leading-relaxed mb-4">
                Jeinar va vérifier ton inscription à la retraite<br />
                et t’envoyer ton lien d’accès personnel<br />
                dans les plus brefs délais.
              </p>
              <p className="text-[#4B3329] leading-relaxed mb-4">
                Ce lien te permettra d’entrer dans ton espace,<br />
                simplement et en toute sécurité.
              </p>
              <p className="text-sm text-[#8C6A4A]">
                Tu peux maintenant fermer cette page en confiance.
              </p>
            </div>
          )}

          {hasError && (
            /* Error State - Cas 2 */
            <div className="text-center animate-fade-in-up">
              <h2 className="font-serif text-2xl text-[#3F2A24] mb-4">
                Merci pour ta demande 🤍
              </h2>
              <p className="text-[#4B3329] leading-relaxed mb-4">
                À ce stade, ton inscription à la retraite<br />
                n’apparaît pas comme validée.
              </p>
              <p className="text-[#4B3329] leading-relaxed mb-4">
                Si tu penses qu’il s’agit d’une confusion<br />
                ou si tu as besoin de clarifier ta situation,<br />
                n’hésite pas à nous écrire à :<br />
                <span className="font-semibold">exquisite.impactful.retreats@gmail.com</span>
              </p>
              <p className="text-sm text-[#8C6A4A]">
                Nous prendrons le temps de te répondre.
              </p>
            </div>
          )}

          {/* Lien de retour */}
          <div className="mt-16 text-center">
            <Link 
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[#8C6A4A] hover:text-[#C5A059] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Retour à la page d’accueil</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
